import { useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Printer } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-hot-toast';
import { OrderPdfPreview } from '../[id]/OrderPdfPreview';
import type { OrderSummary } from '../../../../infrastructure/OMSClient';
import { ShippingProviderType } from '../../../../infrastructure/OMSClient';
import { useShipping } from '../../../../apps/hooks/shipping/useShipping';
import { appendPackingVerificationPages } from '../utils/parxlPackingVerificationPdf';

interface OrderLabelPrintButtonProps {
  order: OrderSummary;
  variant?: string;
  size?: 'sm' | 'lg';
  showText?: boolean;
}

const getCurrentShippingProvider = (order: OrderSummary): ShippingProviderType | null => {
  if (order.shippingData?.provider) return order.shippingData.provider;
  if (order.parxlData) return ShippingProviderType.PARXL;
  if (order.deleoData?.deleoOrderId) return ShippingProviderType.DELEO;
  return null;
};

export function OrderLabelPrintButton({
  order,
  variant = 'outline-primary',
  size = 'sm',
  showText = true,
}: OrderLabelPrintButtonProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const { downloadParxlLabels, isDownloadingLabels } = useShipping();

  const handleDeleoPrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
        size: 27.94cm 21.59cm landscape !important;
        margin: 0 !important;
      }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        html, body {
          width: 27.94cm !important;
          height: 21.59cm !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
        }
        .modal-body,
        .modal-content,
        .modal {
          margin: 0 !important;
          padding: 0 !important;
          max-height: none !important;
          overflow: visible !important;
          border: none !important;
          box-shadow: none !important;
          width: 27.94cm !important;
          height: 21.59cm !important;
        }
        .print-area {
          width: 27.94cm !important;
          height: 21.59cm !important;
          max-width: 27.94cm !important;
          max-height: 21.59cm !important;
          box-sizing: border-box !important;
          margin: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 60px !important;
          padding-left: 15% !important;
          padding-right: 15% !important;
          overflow: hidden !important;
        }
      }
    `,
  });

  const handleParxlPrint = async () => {
    const trackingId = order.parxlData?.plsShipmentReference || order.shippingData?.providerOrderId;
    if (!trackingId) {
      toast.error('No Parxl tracking ID found');
      return;
    }

    setIsPrinting(true);
    try {
      const response = await downloadParxlLabels({ trackingIDs: [trackingId] });
      if (response.success && response.labels && response.labels.length > 0 && response.labels[0]?.pdfData) {
        const byteCharacters = atob(response.labels[0].pdfData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const originalPdfBytes = new Uint8Array(byteNumbers);
        const modifiedPdfBytes = await appendPackingVerificationPages(originalPdfBytes, order);
        const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
      } else {
        toast.error('Failed to get label for printing');
      }
    } catch (error) {
      toast.error(`Failed to print label: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePrint = async () => {
    const provider = getCurrentShippingProvider(order);

    switch (provider) {
      case ShippingProviderType.PARXL:
        await handleParxlPrint();
        break;
      case ShippingProviderType.DELEO:
      default:
        handleDeleoPrint();
        break;
    }
  };

  const provider = getCurrentShippingProvider(order);
  const isLoading = isPrinting || isDownloadingLabels;
  const buttonLabel = provider === ShippingProviderType.PARXL ? 'Print (Parxl)' : 'Print';

  return (
    <>
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }}>
        <OrderPdfPreview ref={printRef} order={order} />
      </div>
      <Button
        variant={variant}
        size={size}
        onClick={handlePrint}
        disabled={isLoading}
        style={{ fontSize: '12px', padding: '4px 12px' }}
      >
        {isLoading ? (
          <Spinner animation="border" size="sm" className="me-1" />
        ) : (
          <Printer className="h-4 w-4 me-1" />
        )}
        {showText && buttonLabel}
      </Button>
    </>
  );
}

